import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyResult, KeyResultType } from '@momentum/types';
import { CircularProgress } from '../../shared/CircularProgress';
import { KPIProgressValue } from '../../shared/KPIProgressValue';
import { Repeat } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { KeyResultHistoryDrawer } from './KeyResultHistoryDrawer';

interface ViewGoalKeyResultsProps {
  keyResults?: KeyResult[];
  startDate?: string;
  dueDate?: string;
}

export const ViewGoalKeyResults: React.FC<ViewGoalKeyResultsProps> = ({
  keyResults,
  startDate,
  dueDate,
}) => {
  const { t } = useTranslation('goals');
  const [selectedKeyResult, setSelectedKeyResult] = useState<KeyResult | null>(
    null
  );
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);

  if (!keyResults || keyResults.length === 0) {
    return null;
  }

  const handleViewHistory = (kr: KeyResult) => {
    setSelectedKeyResult(kr);
    setIsHistoryDrawerOpen(true);
  };

  return (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block">
        {t('keyResultsLabel')}
      </label>
      <div className="space-y-4">
        {keyResults.map((kr, index) => {
          const isMinimum =
            (kr.type ?? KeyResultType.MAXIMUM) === KeyResultType.MINIMUM;

          // For minimum KPIs (like complaints), reverse the progress calculation
          // If you have 7 complaints out of 10, that's 30% progress (3 remaining)
          // Guard against division by zero and clamp to 0-100 range
          const progress =
            kr.quantity > 0
              ? Math.min(
                  Math.max(
                    isMinimum
                      ? ((kr.quantity - kr.currentProgress) / kr.quantity) * 100
                      : (kr.currentProgress / kr.quantity) * 100,
                    0
                  ),
                  100
                )
              : 0;
          const isCompleted = isMinimum
            ? kr.currentProgress <= 0
            : kr.currentProgress >= kr.quantity;

          return (
            <div
              key={index}
              className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
            >
              {/* Recurring Icon Badge */}
              {kr.recurrence && (
                <div className="flex items-center gap-2 mb-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-1 text-primary hover:bg-primary/10"
                    onClick={() => handleViewHistory(kr)}
                  >
                    <Repeat className="h-4 w-4 me-1" />
                    <span className="text-xs">{t('viewHistory')}</span>
                  </Button>
                </div>
              )}

              {/* Title, Progress Value, and Circular Progress */}
              <div className="flex items-center gap-4">
                {/* Title and Value stacked */}
                <div className="flex-1 space-y-2">
                  <h4 className="text-sm font-medium text-gray-900">
                    {kr.title}
                  </h4>
                  <KPIProgressValue
                    currentProgress={kr.currentProgress}
                    quantity={kr.quantity}
                    unit={kr.unit}
                    variant="badge"
                    isCompleted={isCompleted}
                  />
                </div>

                {/* Circular Progress */}
                <CircularProgress
                  progress={progress}
                  startDate={startDate}
                  dueDate={dueDate}
                  size={56}
                  strokeWidth={6}
                  isMinimum={isMinimum}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* History Drawer */}
      {selectedKeyResult && (
        <KeyResultHistoryDrawer
          keyResult={selectedKeyResult}
          isOpen={isHistoryDrawerOpen}
          onClose={() => {
            setIsHistoryDrawerOpen(false);
            setSelectedKeyResult(null);
          }}
        />
      )}
    </div>
  );
};
